<template>
  <div v-if="isVisible" class="invitee-banner">
    <!-- 未ログイン: 紹介URL経由でこれから会員登録する人。キャンペーンの主対象 -->
    <div v-if="state === 'invite'" class="invitee-banner__box">
      <!-- 見出しの吹き出しは「紹介する側」向けの文言なので、被紹介者の立場に差し替える -->
      <referral-campaign-headline :badge-lines="copy.badgeLines" />
      <p class="invitee-banner__lead">{{ copy.lead }}</p>

      <div class="invitee-banner__step">
        <span class="invitee-banner__step-no" aria-hidden="true">{{
          copy.stepRegisterNo
        }}</span>
        <button
          type="button"
          class="invitee-banner__cta"
          @click="onRegisterClick"
        >
          {{ copy.cta }}
        </button>
      </div>

      <span class="invitee-banner__arrow" aria-hidden="true"></span>

      <!-- ②は①のあとでしか押せない。先にヤギプラ加入へ入れると、紹介の紐付け
           （登録 POST）が終わる前に課金されて trial_end に無料日数が乗らない。
           会員登録が済むと state が registered に変わり、押せる CTA になる -->
      <div class="invitee-banner__step">
        <span class="invitee-banner__step-no" aria-hidden="true">{{
          copy.stepPrimeNo
        }}</span>
        <button
          type="button"
          class="invitee-banner__cta invitee-banner__cta--waiting"
          disabled
        >
          {{ copy.primeCta }}
        </button>
      </div>

      <p class="invitee-banner__desc">{{ copy.primeDescription }}</p>

      <!-- 要件5: 必須の3点は訴求の近くに出す。折りたたまない -->
      <ul class="invitee-banner__notes">
        <li v-for="note in copy.notes" :key="note">※{{ note }}</li>
      </ul>

      <router-link class="invitee-banner__terms" :to="campaign.termsPath">
        ▸ {{ copy.termsLabel }}
      </router-link>
    </div>

    <!-- このバナーを見ている最中に会員登録／ログインした人。紐付けはサーバー側で
         済んでいるので、あとはヤギプラに加入すれば30日無料が付く。ここで
         「対象外」を出すと主対象に誤案内することになるので必ず分ける -->
    <div v-else-if="state === 'registered'" class="invitee-banner__box">
      <referral-campaign-headline :badge-lines="copy.badgeLines" />
      <p class="invitee-banner__title">{{ copy.registeredTitle }}</p>
      <p class="invitee-banner__lead">{{ copy.registeredLead }}</p>

      <button
        type="button"
        class="invitee-banner__cta"
        @click="openPrimeDialog"
      >
        {{ copy.registeredCta }}
      </button>

      <ul class="invitee-banner__notes">
        <li v-for="note in copy.notes" :key="note">※{{ note }}</li>
      </ul>

      <router-link class="invitee-banner__terms" :to="campaign.termsPath">
        ▸ {{ copy.termsLabel }}
      </router-link>
    </div>

    <!-- サーバーが被紹介者として受け付けなかった人。既存ヤギプライム会員か、
         すでに別の紹介URLで紐付いている会員で、この紹介URLの特典は付かない -->
    <div v-else class="invitee-banner__box">
      <p class="invitee-banner__title">{{ copy.ineligibleTitle }}</p>
      <p class="invitee-banner__lead">
        {{ copy.ineligibleLead }}<strong>{{ copy.ineligibleLeadStrong }}</strong
        >{{ copy.ineligibleLeadTail }}<br class="invitee-banner__pc-br" />{{
          copy.ineligibleLeadNote
        }}
      </p>
      <router-link class="invitee-banner__terms" :to="campaign.termsPath">
        ▸ {{ copy.termsLabel }}
      </router-link>

      <!-- 対象外の人はこの画面でできることが無いので、行き止まりにせず戻す -->
      <router-link class="invitee-banner__cta invitee-banner__cta--top" to="/">
        {{ copy.ineligibleTopCta }}
      </router-link>
    </div>

    <!-- ログイン／加入ダイアログはどちらも各画面が自前で置く作りで、トップには無い。
         これが無いとイベントを投げても購読者がおらず CTA が無反応になる。
         バナーが出るときだけ載せれば足りる -->
    <LoginDialog />
    <YagiprimePaymentDialog :free-days="pendingDaysTotal" />
  </div>
</template>

<script setup lang="ts">
/**
 * 招待された側がトップに着地したときのバナー（ワイヤー）。
 *
 * `/r/<code>` の着地でコードを localStorage に保存してトップへ replace するので、
 * 「保存済みのコードがある」＝紹介URL経由で来た、と判定できる。
 *
 * ⚠️ ログイン状態だけで「紹介が成立した」と判断してはいけない。
 * このバナーの CTA が開くダイアログは新規登録とログインを兼ねていて、
 * フロントはどちらも同じ経路で referral_code を送り、成功したら
 * clearReferralCode() する。つまり既存会員がログインしただけでも
 * 「コードが消えて認証済みになる」状態が同じように起きる。
 * ここで「30日間無料」と出すと、対象外の人に課金条件を誤案内することになる。
 *
 * 成立の正本はサーバー（referral_attribution）にしかないので、認証後は
 * status API の referee_pending_days を見て、0 より大きいときだけ特典を出す。
 *
 * ⚠️ 引き直すきっかけを isAuthenticated の変化にしてはいけない。Firebase の
 * 認証成功と varuna への登録 POST には時間差があり、認証直後に status を叩くと
 * referral_attribution がまだ無く 0 が返る（＝主対象に「対象外」と誤表示）。
 * POST 完了で飛ぶ VARUNA_LOGIN_SYNCED を待つ。
 *
 * 加入までこの画面のまま済ませる。ヤギプラ加入は
 * $yagiprimePaymentService.useFeature() でモーダルを開く（カードなら 3DS 込みで完結）。
 * ⚠️ useFeature に未ログインのまま入れてはいけない。内蔵フローは
 * 「ログインダイアログが閉じたら加入へ進む」で、上記の登録 POST を待たないため、
 * 紐付く前に加入して trial_end に30日が乗らない事故になる。
 */
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  getCurrentInstance
} from 'vue'
import { useRouter } from 'vue-router/composables'
import { useAuth } from '@/composables/useAuth'
import {
  emitOpenLoginDialog,
  emitter,
  VARUNA_LOGIN_SYNCED
} from '@/plugins/emitter'
import LoginDialog from '@/components/LoginDialog.vue'
import ReferralCampaignHeadline from '@/components/yagiprime/ReferralCampaignHeadline.vue'
import YagiprimePaymentDialog from '@/components/yagiprime/YagiprimePaymentDialog.vue'
import {
  attachReferral,
  fetchReferralStatus
} from '@/services/yagiprime/referral-campaign-client'
import type { ReferralStatusResponse } from '@/services/yagiprime/referral-campaign-client'
import {
  clearReferralCode,
  loadReferralCode
} from '@/utils/yagiprime/referralVisitor'
import campaign from '@/utils/yagiprime/referralCampaign.json'

type BannerState = 'invite' | 'registered' | 'ineligible'

/**
 * authReady の解決を待つ上限。ここを無条件に待つと、Firebase の初期化を
 * 取りこぼした環境でバナーが永久に出ない（＝キャンペーンが黙って死ぬ）。
 * 待ちきれない場合はその時点の認証状態で出し分ける。
 */
const AUTH_WAIT_MS = 5000

/** 加入導線の識別子。route_id / コールバックの紐付けにだけ使う任意文字列 */
const FEATURE_TYPE = 'referral-invitee'

/** 加入後の行き先。加入状況が見える画面へ送る */
const AFTER_SUBSCRIBE_PATH = '/mypage/yagiprime'

const copy = campaign.invitee

type YagiprimePaymentService = {
  init: (vm: unknown) => void
  useFeature: (
    vm: unknown,
    featureType: string,
    options?: { onSuccess?: () => void }
  ) => unknown
}

/**
 * 出し分けが確定したことと、その結果このバナーを出すかどうかを親へ渡す。
 * 着地ページ(/r/<code>)はこれを待ってつなぎ表示を畳む。確定まで数秒〜
 * 数十秒（authReady 5秒 + API 15秒）かかるので、待たずに畳むと空白になる。
 */
const emit = defineEmits<{ (e: 'settled', visible: boolean): void }>()

const props = withDefaults(
  defineProps<{
    /**
     * 「紹介キャンペーンの対象外です」を出すか。紹介URL(/r/<code>)を踏んだ
     * その場では理由を伝える必要があるが、トップのように紹介URL以外から
     * 来た画面では、過去に踏んだコードが localStorage に残っているだけで
     * 本人には関係のない案内になるため出さない
     */
    showIneligible?: boolean
  }>(),
  { showIneligible: true }
)

const vm = getCurrentInstance()!.proxy as unknown as {
  $yagiprimePaymentService: YagiprimePaymentService
}
const router = useRouter()

const { user, isAuthenticated, whenAuthReady } = useAuth()

/** 紹介URL経由で来たか。localStorage は初期化順の都合で mounted 後に読む */
const hasReferral = ref(false)
/** 出し分けの準備ができたか。確定するまで何も出さない */
const settled = ref(false)
/** サーバーが返した、被紹介者として次の加入で無料になる日数。出し分けの判定に使う */
const refereePendingDays = ref(0)
/** 次の加入で実際に無料になる合計日数。加入モーダルの「N日間無料」表示に使う */
const pendingDaysTotal = ref(0)

const state = computed<BannerState>(() => {
  if (!isAuthenticated.value) return 'invite'
  return refereePendingDays.value > 0 ? 'registered' : 'ineligible'
})

// 認証と成立判定が確定する前に出すと、セッション復元中の既存会員へ
// 一瞬 invite（＝対象外の人への特典訴求）が出てしまう
const isVisible = computed(
  () =>
    campaign.isActive &&
    hasReferral.value &&
    settled.value &&
    (props.showIneligible || state.value !== 'ineligible')
)

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

function onRegisterClick(): void {
  emitOpenLoginDialog()
}

/** ヤギプラ加入モーダルを開く。ログイン済みでしか呼ばない */
function openPrimeDialog(): void {
  vm.$yagiprimePaymentService.useFeature(vm, FEATURE_TYPE, {
    onSuccess: () => {
      router.push(AFTER_SUBSCRIBE_PATH).catch(() => {
        // 同一ルートへの遷移等で reject されても致命ではない
      })
    }
  })
}

function applyStatus(status: ReferralStatusResponse | null): void {
  refereePendingDays.value = status?.referee_pending_days || 0
  pendingDaysTotal.value = status?.pending_days_total || 0
}

/** 成立の正本はサーバー。取得できなければ 0 のまま＝特典を訴求しない */
async function syncRefereeState(): Promise<void> {
  const uid = user.value?.uid
  if (!uid) {
    applyStatus(null)
    return
  }
  applyStatus(await fetchReferralStatus(uid))
}

/**
 * ログイン済みのまま紹介URLで来た会員をその場で紐付ける。
 *
 * 紐付けは login の POST に載る referral_code で成立するが、すでに
 * ログインしているブラウザでは認証イベントが起きず login が飛ばない。
 * ここで叩かないと、既存ヤギッシュ会員（ヤギプラ未加入）が紹介URLから
 * 来ても「対象外」表示のままになる。
 *
 * 対象外の会員にはサーバーが紐付けを拒否して現状の status を返すので、
 * 表示の出し分けはこれまでどおりサーバーの値だけで決まる。
 */
async function attachIfAuthenticated(): Promise<boolean> {
  const uid = user.value?.uid
  const code = loadReferralCode()
  if (!uid || !code) return false
  // 紐付けは取り消せないので、サーバーは ID トークンで本人確認する。
  // 取れなかったら紐付けない（コードは消さないので次の機会に再試行できる）
  const idToken = await user.value.getIdToken().catch(() => '')
  if (!idToken) return false
  const status = await attachReferral(uid, code, idToken)
  if (!status) return false
  applyStatus(status)
  // 紐付いたコードは用済み。login 経路と同じく捨てて再送しない
  if (status.is_referee) clearReferralCode()
  return true
}

/**
 * ダイアログでの登録・ログインは mounted の後に起きるので、その完了で引き直す。
 * 紐付けが成立したときだけ、続けて加入モーダルまで開く（対象外の人には開かない）。
 */
async function onVarunaLoginSynced(): Promise<void> {
  await syncRefereeState()
  if (refereePendingDays.value > 0) openPrimeDialog()
}

onMounted(async () => {
  // 登録成功時に clearReferralCode() されるので、読むのは認証を待つ前
  hasReferral.value = !!loadReferralCode()
  await Promise.race([whenAuthReady(), delay(AUTH_WAIT_MS)])
  // 紐付けは status より先。後にすると初回描画が「対象外」になってから覆る
  const attached = await attachIfAuthenticated()
  if (!attached) await syncRefereeState()
  settled.value = true
  emit('settled', isVisible.value)
  // 決済結果の購読・PayPay/Stripe リダイレクト検知。加入モーダルを出す側の責務
  vm.$yagiprimePaymentService.init(vm)
  emitter.on(VARUNA_LOGIN_SYNCED, onVarunaLoginSynced)
})

onBeforeUnmount(() => {
  emitter.off(VARUNA_LOGIN_SYNCED, onVarunaLoginSynced)
})
</script>

<style lang="scss" scoped>
@import '@/assets/scss/yagiprime/referral-campaign-panel';

.invitee-banner {
  margin: 0 0 16px;
}

/* 枠線・背景はシェア枠（ReferralShareOfferBox）と同じキャンペーンの枠を使う。
   見出し（ReferralCampaignHeadline）ごと同じ見た目にするため、独自の枠は持たない */
.invitee-banner__box {
  @include referral-campaign-panel;
}

.invitee-banner__title {
  margin: 0 0 8px;
  color: #222;
  font-size: 16px;
  font-weight: bold;
}

/* 見出し（対比図）の直後だけ離す。対象外表示には見出しが無いので、
   見出し前提の余白を常に持たせると枠の上が間延びする */
.referral-headline + .invitee-banner__title {
  margin-top: 24px;
}

.invitee-banner__lead {
  /* 上の特典ボックス（対比図）と詰まって見えるので間を空ける */
  margin: 24px 0 12px;
  color: #444;
  font-size: 14px;
  line-height: 1.6;
}

/* 番号とボタンの行。ボタンの左に①②を置いても、行全体は上の特典ボックス
   （.referral-headline__benefit）と同じ 520px に収める */
.invitee-banner__step {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  max-width: 520px;
  margin: 0 auto;
}

.invitee-banner__step-no {
  flex: 0 0 auto;
  color: #e3165c;
  font-size: 20px;
  font-weight: bold;
}

/* ①→② の順であることを示す下矢印 */
.invitee-banner__arrow {
  display: block;
  width: 0;
  height: 0;
  margin: 10px auto;
  border-top: 12px solid #e3165c;
  border-right: 10px solid transparent;
  border-left: 10px solid transparent;
}

/* 色はシェア枠の「URLをシェア」ボタンと同じピンク */
.invitee-banner__cta {
  flex: 1 1 auto;
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid #e3165c;
  border-radius: 8px;
  background: #e3165c;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
}

.invitee-banner__cta:hover {
  color: #fff;
  text-decoration: none;
  opacity: 0.85;
}

/* ①が済むまで押せない②。押せる見た目にすると、紐付く前の加入を誘ってしまう */
.invitee-banner__cta--waiting:disabled,
.invitee-banner__cta--waiting:disabled:hover {
  opacity: 0.4;
  cursor: default;
}

/* ②のヤギプライムが何なのかを、加入を決める前に読める位置で補う。
   幅はボタンの行（.invitee-banner__step）に合わせて読みやすさを揃える */
.invitee-banner__desc {
  max-width: 520px;
  margin: 12px auto 0;
  color: #666;
  font-size: 13px;
  line-height: 1.7;
  text-align: left;
}

.invitee-banner__notes {
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  color: #e0407a;
  font-size: 12px;
  line-height: 1.7;
}

/* PC だけ「くわしくは〜」の前で改行する。SP は幅が狭く自然に折り返すので出さない */
.invitee-banner__pc-br {
  display: inline;
}

.invitee-banner__terms {
  display: inline-block;
  margin-top: 8px;
  color: #666;
  font-size: 12px;
}

/* 単独で置くトップ導線。番号付きの行と違い flex の子ではないので幅を自分で持つ */
.invitee-banner__cta--top {
  display: block;
  max-width: 520px;
  margin: 16px auto 0;
}

@media screen and (width <= 480px) {
  .invitee-banner__pc-br {
    display: none;
  }
}
</style>
